import { Link, useNavigate } from 'react-router-dom'
import { useEffect } from 'react'
import { Button } from '../components/ui/button'
import { ThemeToggle } from '../components/ui/theme-toggle'
import Logo from '../components/ui/logo'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { useTheme } from '../contexts/ThemeContext'
import { useAuth } from '../hooks/useAuth'
import { Icon } from '../components/ui/icon'

const Home = () => {
	const { theme } = useTheme()
	const { isAuthenticated, user, getUserType } = useAuth()
	const navigate = useNavigate()

	useEffect(() => {
		// Redirect authenticated users to their respective dashboards
		if (isAuthenticated && user) {
			const userType = getUserType()
			const routeMap = {
				'default-administrator': '/admin',
				'administrator': '/admin',
				'driver': '/driver/dashboard',
				'passenger': '/passenger/dashboard',
			}
			navigate(routeMap[userType as keyof typeof routeMap] || '/admin')
		}
	}, [isAuthenticated, user, getUserType, navigate])

	const userTypeCards = [
		{
			type: 'default-administrator',
			title: 'Default Administrator',
			description: 'Basic admin login with essential access',
			icon: 'cog',
			color: 'bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800',
			route: '/login?type=default-administrator'
		},
		{
			type: 'administrator',
			title: 'Administrator',
			description: 'Full admin access with advanced features',
			icon: 'user-2',
			color: 'bg-purple-50 dark:bg-purple-950 border-purple-200 dark:border-purple-800',
			route: '/login?type=administrator'
		},
		// {
		// 	type: 'driver',
		// 	title: 'Driver Portal',
		// 	description: 'Access your driving dashboard and earnings',
		// 	icon: 'car',
		// 	color: 'bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800',
		// 	route: '/login?type=driver'
		// },
		// {
		// 	type: 'passenger',
		// 	title: 'Passenger Portal',
		// 	description: 'Manage your rides and profile',
		// 	icon: 'user-group',
		// 	color: 'bg-orange-50 dark:bg-orange-950 border-orange-200 dark:border-orange-800',
		// 	route: '/login?type=passenger'
		// }
	]

	return (
		<div className="relative min-h-screen bg-background">
			<div className="absolute right-2 top-9 my-auto ml-4">
				<ThemeToggle />
			</div>

			<header className="container mx-auto px-4 py-6">
				<nav className="flex items-center justify-between">
					<Logo
						key={theme}
						src={
							theme === 'dark'
								? '/images/Ubuntu_cabwhite.png' 
								: '/images/ubuntucab_green.png' 
						}
						alt="Ubuntucabs"
						className="logo-class w-36 h-16"
					/>
				</nav>
			</header>

			<main className="container mx-auto flex min-h-[calc(100vh-150px)] flex-col items-center justify-center px-4 py-12">
				<div className="text-center mb-12">
					<h1 className="mb-6 text-4xl font-extrabold tracking-tight sm:text-5xl md:text-6xl">
						Welcome to Ubuntucabs
					</h1>
					<p className="mb-8 max-w-3xl text-xl text-muted-foreground">
						Your comprehensive ride-hailing platform. Choose your portal below to access 
						tailored features for administrators, drivers, and passengers.
					</p>
				</div>

				{/* User Type Selection Cards */}
				{/* <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6 w-full max-w-6xl mb-8">
					{userTypeCards.map((card) => (
						<Card 
							key={card.type} 
							className={`${card.color} hover:shadow-lg transition-all duration-200 cursor-pointer group`}
							onClick={() => navigate(card.route)}
						>
							<CardHeader className="text-center pb-3">
								<div className="mx-auto mb-3 p-3 rounded-full bg-white dark:bg-gray-800 w-fit group-hover:scale-110 transition-transform">
									<Icon 
										name={card.icon as any} 
										className="h-8 w-8 text-primary" 
									/>
								</div>
								<CardTitle className="text-lg font-semibold">
									{card.title}
								</CardTitle>
							</CardHeader>
							<CardContent className="text-center pt-0">
								<CardDescription className="text-sm">
									{card.description}
								</CardDescription>
							</CardContent>
						</Card>
					))}
				</div> */}

				{/* Quick Login Button */}
				<div className="flex flex-col items-center gap-4">
					<Link to="/login?type=administrator">
						<Button size="lg" className="px-8">
						Login
						</Button>
					</Link>
					<p className="text-sm text-muted-foreground">
						{/* Or select a specific portal above for a tailored experience */}
					</p>
				</div>

				{/* Feature Highlights */}
				<div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 w-full max-w-4xl">
					<div className="text-center">
						<div className="mx-auto mb-4 p-3 rounded-full bg-primary/10 w-fit">
							<Icon name="database" className="h-6 w-6 text-primary" />
						</div>
						<h3 className="font-semibold mb-2">Real-time Monitoring</h3>
						<p className="text-sm text-muted-foreground">
							Track rides, earnings, and performance in real-time
						</p>
					</div>
					<div className="text-center">
						<div className="mx-auto mb-4 p-3 rounded-full bg-primary/10 w-fit">
							<Icon name="cog" className="h-6 w-6 text-primary" />
						</div>
						<h3 className="font-semibold mb-2">Secure Access</h3>
						<p className="text-sm text-muted-foreground">
							Multi-role authentication with enhanced security
						</p>
					</div>
					<div className="text-center">
						<div className="mx-auto mb-4 p-3 rounded-full bg-primary/10 w-fit">
							<Icon name="user-group" className="h-6 w-6 text-primary" />
						</div>
						<h3 className="font-semibold mb-2">Community Driven</h3>
						<p className="text-sm text-muted-foreground">
							One ride, community driven - connecting people
						</p>
					</div>
				</div>
			</main>
		</div>
	)
}

export default Home
